import React, { useState } from "react";
import { Dialog, DialogTitle, IconButton, Tooltip } from "@material-ui/core";
import { FadeLoader } from "react-spinners";
import Button from "../../components/CustomButtons/Button.jsx";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import CustomPDFViewer from "../../components/CustomPDFViewer/CustomPDFViewer";
import axios_pr from "store/axios/axios-pr";
import { calculateTotalInvoicePicture } from "../Invoices/InvoiceLogic";
import { renderReportHTML } from "./paymentReportsLogic";
import { clone, handleError } from "../../shared/utility";
import { getUser } from "../../shared/authValidation";

export const isEstimatePermitted = () => {
    const u = getUser();
    if (!u) return false;
    if (u.name === 'CRS Dev') return true;
    if (!u.permObj) return false;
    return !!(u.permObj['Invoice preview_payment_report_estimate'] || u.permObj['PR preview_payment_report_estimate']);
};

export const injectPreviewWatermark = (html) => {
    const watermarkStyle = `<style>
        .preview-watermark {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 99999;
        }
        .preview-watermark span {
            font-size: 140px;
            color: rgba(218, 34, 41, 0.18);
            font-weight: 900;
            transform: rotate(-30deg);
            letter-spacing: 12px;
            white-space: nowrap;
            font-family: Arial, sans-serif;
        }
        .preview-banner {
            position: fixed;
            top: 8px;
            right: 8px;
            background: #da2229;
            color: #fff;
            font-weight: bold;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 11px;
            letter-spacing: 1px;
            z-index: 99999;
            font-family: Arial, sans-serif;
        }
    </style>`;
    const watermarkBody = `<div class="preview-watermark"><span>PREVIEW</span></div><div class="preview-banner">PREVIEW — NOT A FINAL REPORT</div>`;
    let out = html;
    if (out.indexOf('</head>') !== -1) {
        out = out.replace('</head>', watermarkStyle + '</head>');
    } else {
        out = watermarkStyle + out;
    }
    if (out.indexOf('<body>') !== -1) {
        out = out.replace('<body>', '<body>' + watermarkBody);
    } else {
        out = watermarkBody + out;
    }
    return out;
};

const EstimatedPaymentReportButton = (props) => {
    const { rs_id } = props;
    const [open, setOpen] = useState(false);
    const [rsData, setRsData] = useState(null);
    const [pdf, setPdf] = useState(null);
    const [generating, setGenerating] = useState(false);

    const permitted = isEstimatePermitted();
    if (!permitted) return null;
    if (!rs_id) return null;

    const openModal = async () => {
        setOpen(true);
        setRsData(null);
        try {
            const all_data = await calculateTotalInvoicePicture(rs_id);
            setRsData(all_data);
        } catch (e) {
            handleError(e);
        }
    };

    const closeModal = () => {
        setOpen(false);
        setRsData(null);
        setPdf(null);
    };

    const previewPayment = async (invoice, payment) => {
        if (!rsData) return;
        setGenerating(true);
        try {
            let workingRsData = rsData;

            let payment_for_preview = payment;
            if (!payment_for_preview) {
                const amountFromLineItems = Array.isArray(invoice.data && invoice.data.line_items)
                    ? invoice.data.line_items.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0)
                    : 0;
                const due = parseFloat(invoice.total_amount_due_this_invoice)
                    || parseFloat(invoice.total_amount_this_invoice)
                    || parseFloat(invoice.total_balance_due_now)
                    || amountFromLineItems
                    || 0;
                payment_for_preview = {
                    id: `estimate-${invoice.id}`,
                    invoice_id: invoice.id,
                    payment_amount: due,
                    check_number: 'ESTIMATE',
                    date_received: new Date().toISOString().split('T')[0],
                    payments: [],
                    _is_estimate: true
                };
                workingRsData = clone(workingRsData);
                const targetInvoice = (workingRsData.invoices || []).find(inv => inv.id === invoice.id);
                if (targetInvoice) {
                    if (!Array.isArray(targetInvoice.payments)) targetInvoice.payments = [];
                    targetInvoice.payments.push(payment_for_preview);
                }
            }

            const chosen = {
                invoice_ids: [invoice.id],
                payment_ids: [payment_for_preview.id]
            };
            const obj = renderReportHTML(workingRsData, chosen);
            const stamped = injectPreviewWatermark(obj.finalHTML);
            const resp = await axios_pr.post('/pdf', { htmlContent: stamped });
            if (resp && resp.data) setPdf(resp.data);
        } catch (e) {
            handleError(e);
            alert('Could not generate estimate preview.');
        }
        setGenerating(false);
    };

    return (
        <>
            <IconButton onClick={openModal}>
                <Tooltip title="Preview Estimated Payment Report"><span style={{ fontWeight: 'bold', fontSize: 13 }}>EST</span></Tooltip>
            </IconButton>

            {open && (
                <Dialog open={true} keepMounted onClose={closeModal} fullWidth={true} maxWidth="md">
                    <DialogTitle>
                        <div style={{ textAlign: 'center' }}>PREVIEW — ESTIMATED PAYMENT REPORT</div>
                    </DialogTitle>
                    <div style={{ padding: 20 }}>
                        {!rsData && <div style={{ textAlign: 'center' }}><FadeLoader /></div>}
                        {rsData && (
                            <GridContainer>
                                {(rsData.invoices || []).map((inv, idx) => (
                                    <GridItem xs={12} sm={12} md={12} key={`est-inv-${inv.id}`}>
                                        <div className="boxed" style={{ padding: 12, marginBottom: 12, border: '1px solid #ddd' }}>
                                            <Button color="primary" style={{ backgroundColor: '#000', color: '#fff', marginBottom: 10 }}>
                                                INVOICE #{inv.invoice_num || idx + 1}
                                            </Button>
                                            <div style={{ marginLeft: 12 }}>
                                                {(inv.payments && inv.payments.length > 0)
                                                    ? inv.payments.map((p, pIdx) => (
                                                        <Button key={`est-pay-${inv.id}-${p.id}`} color="white" style={{ marginRight: 8, marginTop: 6 }}
                                                            onClick={() => previewPayment(inv, p, pIdx)}
                                                            disabled={generating}
                                                        >
                                                            PAYMENT #{pIdx + 1}
                                                        </Button>
                                                    ))
                                                    : (
                                                        <Button color="white" style={{ marginTop: 6 }}
                                                            onClick={() => previewPayment(inv, null, 0)}
                                                            disabled={generating}
                                                        >
                                                            PAYMENT #1 (simulated)
                                                        </Button>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </GridItem>
                                ))}
                                {(!rsData.invoices || rsData.invoices.length === 0) && (
                                    <GridItem xs={12} sm={12} md={12}>
                                        <em>No invoices on this RS yet.</em>
                                    </GridItem>
                                )}
                            </GridContainer>
                        )}
                        {generating && <div style={{ textAlign: 'center', marginTop: 10 }}>Generating preview...</div>}
                    </div>
                </Dialog>
            )}

            {pdf && <CustomPDFViewer
                pdfData={pdf}
                open={true}
                close={() => setPdf(null)}
            />}
        </>
    );
};

export default EstimatedPaymentReportButton;
